import React, { useState, useEffect, useRef } from 'react';
import { TextField, TextFieldProps, Box } from '@mui/material';
import { FormFieldError } from './FormFieldError';
import { FieldHint } from '../validation/FieldHint';
import { hasValidationRules } from '../../utils/validationMessages';

interface ValidatedTextFieldProps extends Omit<TextFieldProps, 'error'> {
  fieldError?: string;
  onBlurValidation?: (value: any) => void;
  showHints?: boolean; // Whether to show validation hints on focus
}

/**
 * ValidatedTextField wraps Material-UI TextField with enhanced error handling,
 * validation hints, and accessibility features.
 * 
 * Features:
 * - Automatic error state management
 * - Validation hints on focus
 * - Error highlight animation
 * - ARIA attributes for accessibility
 * - Optional blur validation callback
 * 
 * @param fieldError - Error message to display
 * @param onBlurValidation - Callback function called when field loses focus
 * @param showHints - Whether to show validation hints on focus (default: true)
 * @param ...textFieldProps - All standard TextField props
 */
export const ValidatedTextField: React.FC<ValidatedTextFieldProps> = ({
  fieldError,
  onBlurValidation,
  showHints = true,
  helperText,
  ...textFieldProps
}) => {
  const fieldId = textFieldProps.id || textFieldProps.name;
  const fieldName = textFieldProps.name || '';
  const hasError = Boolean(fieldError);
  const [isFocused, setIsFocused] = useState(false);
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // Show hints only if field has validation rules
  const shouldShowHints = showHints && hasValidationRules(fieldName);

  // Trigger highlight animation when error appears
  useEffect(() => {
    if (hasError) {
      setShouldHighlight(true);
      const timer = setTimeout(() => setShouldHighlight(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [hasError]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    textFieldProps.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);

    // Call original onBlur if provided
    textFieldProps.onBlur?.(e);

    // Call validation callback if provided
    if (onBlurValidation) {
      onBlurValidation(e.target.value);
    }
  };

  return (
    <Box
      ref={elementRef}
      sx={{
        position: 'relative',
        animation: shouldHighlight && hasError ? 'highlight-pulse 2s ease-out' : 'none',
        '@keyframes highlight-pulse': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)',
          },
          '50%': {
            boxShadow: '0 0 0 8px rgba(211, 47, 47, 0)',
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
          },
        },
      }}
    >
      <TextField
        {...textFieldProps}
        error={hasError}
        helperText={!hasError ? helperText : undefined}
        onFocus={handleFocus}
        onBlur={handleBlur}
        slotProps={{
          ...textFieldProps.slotProps,
          input: {
            ...textFieldProps.slotProps?.input,
            'aria-invalid': hasError,
            'aria-describedby': hasError ? `${fieldId}-error` : undefined,
          },
        }}
        sx={{
          ...textFieldProps.sx,
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: 'error.main',
          },
        }}
      />

      {/* Show error message if present */}
      {hasError && <FormFieldError error={fieldError} fieldId={fieldId!} />}

      {/* Show validation hints on focus (if no error) */}
      {!hasError && shouldShowHints && (
        <FieldHint
          fieldName={fieldName}
          isFocused={isFocused}
          disabled={textFieldProps.disabled}
        />
      )}
    </Box>
  );
};
